import type { Maybe } from "@entrilia/shared/types";

export enum CurrencySource {
  Static = "Static",
  Field = "Field",
}

export enum NegativeValueAs {
  Minus = "Minus",
  Parentheses = "Parentheses",
}

export enum ZeroValueAs {
  Zero = "Zero",
  Dash = "Dash",
  Empty = "Empty",
}

export type NumberFormatterOptions = {
  formatType: FormatType;
  type: string;
  abbreviation: string | undefined;
  decimals: number | undefined;
  autoDecimals: boolean | undefined;
  percentage: boolean | undefined;
  convertToPercentage: boolean | undefined;
  currency: string;
  prefix: string | undefined;
  suffix: string | undefined;
  referenceDimensionName: string | undefined;
  currencySource: CurrencySource;
  showNegativeValuesAs?: NegativeValueAs;
  showZeroValuesAs?: ZeroValueAs;
};

export enum FormatType {
  None = "None",
  Numeric = "Numeric",
  Date = "Date",
  DateTime = "DateTime",
  BlankIfZero = "BlankIfZero",
  BlankIfFalse = "BlankIfFalse",
  Boolean = "Boolean",
  Option = "Option",
  Currency = "Currency",
}

export enum CalculateByField {
  Lcy = "Lcy",
  Pcy = "Pcy",
  Shares = "Shares",
  AdditionalLcy = "AdditionalLcy",
  Scy = "Scy",
}

export enum AmountType {
  Net = "Net",
  Credit = "Credit",
  Debit = "Debit",
}

export enum ConditionEqualityType {
  Equal = "Equal",
  NotEqual = "NotEqual",
  LessThan = "LessThan",
  LessThanOrEqual = "LessThanOrEqual",
  Between = "Between",
  GreaterThan = "GreaterThan",
  GreaterThanOrEqual = "GreaterThanOrEqual",
  Contains = "Contains",
  DoesntContain = "DoesntContain",
}

export type ConditionOperator = "And" | "Or";

export enum MeasureDateRange {
  NetChange = "NetChange",
  BeginningBalance = "BeginningBalance",
  EndingBalance = "EndingBalance",
  Mtd = "Mtd",
  Qtd = "Qtd",
  Ytd = "Ytd",
  Ltm = "Ltm", // Last 12 months
}

export enum Grouping {
  ByYear = "ByYear",
  ByMonth = "ByMonth",
  ByQuarter = "ByQuarter",
}

export type ConditionFilterDescriptor = {
  equalityType?: ConditionEqualityType;
  dimensionName: string;
  values: (string | null)[];
};
export interface ConditionDescriptor {
  filter?: ConditionFilterDescriptor;
  operator?: ConditionOperator;
  conditions: ConditionDescriptor[];
}
export type ConditionConfiguration = {
  guid: string;
  filter: ConditionFilterDescriptor;
  parameter?: boolean | undefined;
  mandatory?: boolean | undefined;
  invalid?: boolean | undefined;
  customLabel?: string;
  systemLabel?: string;
  customId?: string;
};

export type PostCondition = {
  conditionType: PostConditionType;
  value: unknown;
};

export enum PostConditionType {
  Is = "Is",
  IsNot = "IsNot",
}

export enum IrrCashFlowFrequency {
  Daily = "Daily",
  Monthly = "Monthly",
  Quarterly = "Quarterly",
}

export enum FieldConfigurationType {
  Measure = "Measure",
  Irr = "Irr",
  Aggregation = "Aggregation",
  Dimension = "Dimension",
  Metric = "Metric",
  Custom = "Custom",
}

export enum CalculationType {
  PercentageOfTotal = "PercentageOfTotal",
  CustomFormula = "CustomFormula",
  ConditionValue = "ConditionValue",
}

export type ColumnHorizontalAlignment = "Left" | "Center" | "Right";

export interface FieldCustomizations {
  autosize?: boolean;
  columnWidth?: number | null;
  horizontalAlignment?: ColumnHorizontalAlignment | null;
}

export interface FieldConfiguration {
  customizations?: FieldCustomizations | null;
  name: string;
  persistedAggregationName?: string;
  guid: string;
  type: FieldConfigurationType;
  format?: NumberFormatterOptions | undefined | null;
  standalone?: boolean | undefined | null;
  reverseSign?: boolean | undefined | null;
  blankZero?: boolean | undefined | null;
  useCustomConditions?: boolean | undefined | null;
  usePostConditions?: boolean | undefined | null;
  hideAggregation?: boolean | undefined | null;
  hideBlankValues?: boolean | undefined | null;
  hideZeroValues?: boolean | undefined | null;
  dateRange?: MeasureDateRange | undefined | null;
  customLabel?: string | undefined | null;
  systemLabel?: string | undefined | null;
  customConditions?: ConditionConfiguration[] | undefined | null;
  postConditions?: PostCondition[] | undefined | null;
  linkedConditions?: string[] | undefined | null;
  calculateByField?: CalculateByField | undefined | null;
  amountType?: AmountType | undefined | null;
  includeCashFlowDetails?: boolean;
  cashFlowFrequency?: IrrCashFlowFrequency | undefined | null;
  periodDateField?: Maybe<string>;
  grouping?: Grouping | undefined | null;
  style?: PivotFieldStyle | undefined | null;
  displayValues?: string[] | undefined | null;
  showLogoIcon?: boolean | undefined | null;
  aggregation?: BiAggregation | undefined | null;
  customId?: string | undefined | null;
  calculationType?: CalculationType | undefined | null;
  formulaParameters?: string[] | undefined | null;
  formula?: string | undefined | null;
  blankIfMissingValue?: boolean | undefined | null;
  // For aggregation fields: GUID of another report field (a measure) whose period + unit filters
  // additionally scope this aggregation (Earliest/Latest of a date over only that measure's postings).
  dependsFromFieldId?: string | undefined | null;
}

type PivotFieldStyle = {
  fontStyle?: string[] | undefined | null;
  showTotals?: boolean | undefined | null;
};

export enum BiAggregation {
  None = "None",
  Count = "Count",
  CountDistinct = "CountDistinct",
  Sum = "Sum",
  Min = "Min",
  Max = "Max",
  Average = "Average",
  StringAgg = "StringAgg",
}
